import React, { Fragment, useMemo, useState } from 'react';
import T from 'prop-types';
// redux
import { connect } from 'react-redux';
// components
import ActivitySwitcher from 'components/Activities/ActivitySwitcher';
import FarmFeedHiddenPosts from './FarmFeedHiddenPosts';
import Preloader from 'components/Preloader';
import Waypoint from 'react-waypoint';
import { FormattedMessage } from 'react-intl';
// utils
import moment from 'moment';
import { isUntaggedDirectPost } from 'utils/activityHelper';
// regional date handling
import { getCurrentCountryCode } from 'selectors/regionalDateSelectors';
import { formatDateForUser } from 'utils/regionalDateHelper';
// styles
import './FarmFeedActivities.scss';

const TimeLineSeparator = ({ date, countryCode }) => {
  if (moment(date).isSame(moment(), 'day')) return null;

  return (
    <div className="TimeLineSeparator">
      {(moment(date).isSame(moment().subtract(1, 'days'), 'day'))
        ? <FormattedMessage id="container.farmfeed.timeLine.yesterday" />
        : formatDateForUser(date, countryCode)}
    </div>
  );
};

TimeLineSeparator.propTypes = {
  date: T.string,
  countryCode: T.string,
};

// Consecutive activities of the same day, feed order preserved
const groupActivitiesByDay = (activities) => activities.reduce((groups, activity) => {
  const activityDay = moment(activity.updated_at).format('YYYY-MM-DD');
  const lastGroup = groups[groups.length - 1];
  // the feed is sorted by updated_at desc, so an event refreshed in place (new comment, new diagnosis)
  // can carry a newer day than its neighbours: keep it in the current group instead of splitting the day
  const dayKey = lastGroup && activityDay > lastGroup.dayKey ? lastGroup.dayKey : activityDay;
  if (lastGroup && lastGroup.dayKey === dayKey) {
    lastGroup.activities.push(activity);
  } else {
    groups.push({ dayKey, date: activity.updated_at, activities: [activity] });
  }
  return groups;
}, []);

const FarmFeedActivities = ({
  activities,
  currentUser,
  hideUntaggedPosts,
  loadMoreActivities,
  markAsSeen,
  isLoading,
  meta,
  countryCode,
}) => {
  const [expandedDays, setExpandedDays] = useState({});
  const total = meta?.total || 0;
  const hasMoreItems = activities.length < total;
  const dayGroups = useMemo(() => groupActivitiesByDay(activities), [activities]);

  const toggleDay = (dayKey) => {
    setExpandedDays((prevState) => ({ ...prevState, [dayKey]: !prevState[dayKey] }));
  };

  // expanded hidden posts get marked as seen without touching the sidebar counter, which never counted them
  const renderActivity = (activity, key, isHiddenPost = false) => (
    <ActivitySwitcher
      key={key}
      activity={activity}
      currentUser={currentUser}
      markAsSeen={() => markAsSeen(activity.id, { countable: !isHiddenPost })}
    />
  );

  const renderDayGroup = (group, groupIndex) => {
    const { dayKey, date, activities: dayActivities } = group;
    const isHidden = (activity) => hideUntaggedPosts && isUntaggedDirectPost(activity, currentUser);
    const hiddenCount = dayActivities.filter(isHidden).length;
    const isExpanded = Boolean(expandedDays[dayKey]);
    const visibleActivities = hiddenCount && !isExpanded
      ? dayActivities.filter((activity) => !isHidden(activity))
      : dayActivities;

    return (
      <Fragment key={dayKey + '-' + groupIndex}>
        <TimeLineSeparator date={date} countryCode={countryCode} />
        {visibleActivities.map((activity, index) => (
          renderActivity(activity, dayKey + '-' + activity.id + '-' + index, isHidden(activity))
        ))}
        {!!hiddenCount && (
          <FarmFeedHiddenPosts count={hiddenCount} isExpanded={isExpanded} onToggle={() => toggleDay(dayKey)} />
        )}
      </Fragment>
    );
  };

  return (
    <div>
      {!!activities.length && dayGroups.map(renderDayGroup)}
      {!isLoading && hasMoreItems &&
        <Waypoint onEnter={loadMoreActivities} />}
      {!!activities.length && hasMoreItems &&
        <Preloader isActive={isLoading} className="load-more" />}
    </div>
  );
};

FarmFeedActivities.propTypes = {
  activities: T.array.isRequired,
  loadMoreActivities: T.func.isRequired,
  markAsSeen: T.func.isRequired,
  isLoading: T.bool,
  hideUntaggedPosts: T.bool,
  currentUser: T.object.isRequired,
  meta: T.object,
  countryCode: T.string,
};

FarmFeedActivities.defaultProps = {
  hideUntaggedPosts: false,
};

export default connect(
  (state) => ({
    countryCode: getCurrentCountryCode(state),
  })
)(FarmFeedActivities);
